import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from "@angular/core";
import { ReactiveFormsModule, FormsModule } from "@angular/forms";
import { HttpClientModule } from "@angular/common/http";
import { CommonModule } from "@angular/common";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { InlineSVGModule } from "ng-inline-svg";

import {
  GridModule,
  RowDDService,
  PageService,
  SortService,
  FilterService,
  ExcelExportService,
  EditService,
  ResizeService,
  ToolbarService,
  ColumnChooserService,
  AggregateService,
  ColumnMenuService,
  DetailRowService,
  SelectionService,
  PagerModule,
  GroupService,
  GridAllModule,
} from "@syncfusion/ej2-angular-grids";
import {
  SwitchModule,
  CheckBoxModule,
  RadioButtonModule,
  ButtonModule,
  ChipListModule,
} from "@syncfusion/ej2-angular-buttons";
import { DiagramModule } from "@syncfusion/ej2-angular-diagrams";
import { TooltipModule, DialogModule } from "@syncfusion/ej2-angular-popups";
import {
  DatePickerModule,
  DateRangePickerModule,
  DateTimePickerModule,
} from "@syncfusion/ej2-angular-calendars";

import {
  TextBoxModule,
  ColorPickerModule,
  UploaderModule,
  NumericTextBoxModule,
} from "@syncfusion/ej2-angular-inputs";
import {
  DropDownListModule,
  MultiSelectAllModule,
  AutoCompleteModule,
  DropDownTreeModule,
  ListBoxModule,
} from "@syncfusion/ej2-angular-dropdowns";
import {
  PivotViewAllModule,
  PivotFieldListAllModule,
} from "@syncfusion/ej2-angular-pivotview";
import {
  ChartAllModule,
  AccumulationChartAllModule,
  RangeNavigatorAllModule,
  AccumulationChartModule,
  PieSeriesService,
  AccumulationLegendService,
  AccumulationTooltipService,
  AccumulationAnnotationService,
  AccumulationDataLabelService,
} from "@syncfusion/ej2-angular-charts";
import { DashboardLayoutModule } from "@syncfusion/ej2-angular-layouts";
import {
  CarouselAllModule,
  ToolbarModule,
  TabModule,
  TreeViewModule,
} from "@syncfusion/ej2-angular-navigations";
import {
  PdfViewerModule,
  LinkAnnotationService,
  BookmarkViewService,
  MagnificationService,
  ThumbnailViewService,
  ToolbarService as PbfToolbarService,
  NavigationService,
  TextSearchService,
  TextSelectionService,
  PrintService,
} from "@syncfusion/ej2-angular-pdfviewer";
import {
  DropDownButtonModule,
  SplitButtonModule,
} from "@syncfusion/ej2-angular-splitbuttons";
import {
  RichTextEditorAllModule,
  HtmlEditorService,
  ToolbarService as RichEditorToolbarService,
} from "@syncfusion/ej2-angular-richtexteditor";

import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatDialogModule } from "@angular/material/dialog";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { MatTooltipModule } from "@angular/material/tooltip";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatSelectModule } from "@angular/material/select";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { MatButtonModule } from "@angular/material/button";


// services
import { CommonFunctionService } from "./service/common-function.service";
import { IconLoaderService } from "./service/icon-loader.service";
import { DialogService } from "./service/dialog.service";
import { TranslationService } from "./service/translation.service";

import { CommonGridComponent } from "./common-grid/index.component";
import { GridActionComponent } from "./common-grid/grid-action/grid-action.component";
import { GridActionItemComponent } from "./common-grid/grid-action/grid-action-item/grid-action-item.component";
import { DateTreeFilterPopoverComponent } from "./date-tree-filter/date-tree-filter-popover/index.component";

import { FloatBoxComponent } from "./float-box/index.component";

import { RsPageListComponent } from "./layout/page-list/index.component";
import { RsPageTabComponent } from "./layout/page-tab/index.component";
import { GridBoxComponent } from "./layout/grid-box/index.component";
import { MainContainerComponent } from "./layout/main-container/index.component";
import { RSHeaderComponent } from "./layout/rs-header/index.component";
import { RSFooterComponent } from "./layout/rs-footer/index.component";
import { RSAsideComponent } from "./layout/rs-aside/index.component";
import { MultiTabComponent } from "./layout/multi-tab/index.component";
import { DrawerComponent } from "./layout/drawer/index.component";
import { RSStepperComponent } from "./layout/rs-stepper/index.component";

import { TagInputComponent } from "./form/tag-input/index.component";
import { RadioGroupComponent } from "./form/radio-group/index.component";
import { CheckboxGroupComponent } from "./form/checkbox-group/index.component";
import { SwitchInputComponent } from "./form/switch-input/index.component";
import { EncryptedInputComponent } from "./form/encrypted-input/index.component";
import { SearchInputComponent } from "./form/search-input/index.component";
import { ConfirmSelectComponent } from "./form/confirm-select/index.component";
import { RichtexteditorComponent } from "./form/richtexteditor/index.component";

import { NewActionNotificationComponent } from "./dialog/new-action-notification/new-action-notification.component";
import { CommonDialogComponent } from "./dialog/common-dialog/index.component";
import { CommonDeleteComponent } from "./dialog/common-delete-dialog/index.component";
import { DrawerService } from "./service/drawer.service";
import { DrawerFormComponent } from "./form/drawer-form/drawer-form.component";
import { ToolbarItemComponent } from "./actions/toolbar-item/index.component";
import { RSToolbarComponent } from "./actions/toolbar/index.component";

import { RsCommentaryComponent } from "./commentary/index.component";
import { RSNavCardGroupComponent } from "./layout/nav-card-group/index.component";
import { TruncatedTextToggleComponent } from "./truncated-text-toggle/index.component";
import { SmartPopupComponent } from "./smart-popup/index.component";

import { PaneGroupNewComponent } from "./dashboard/pane-group-new.component";
import { SidebarIconlistNewComponent } from "./dashboard/sidebar-iconlist/sidebar-iconlist-new.component";
import { EmptyIconPromptComponent } from "./dashboard/dialog-group/empty-icon-prompt/empty-icon-prompt.component";
import { GadgetTableComponent } from "./dashboard/gadget-group/gadget-table/gadget-table.component";
import { GadgetPivotComponent } from "./dashboard/gadget-group/gadget-pivot/gadget-pivot.component";
import { GadgetPivotChartComponent } from "./dashboard/gadget-group/gadget-pivot-chart/gadget-pivot.component";
import { GadgetTransposeComponent } from "./dashboard/gadget-group/gadget-transpose/gadget-transpose.component";
import { PortletTypeNewComponent } from "./dashboard/sidebar-iconlist/portlet-type-new/portlet-type-new.component";
import { FieldFilterComponent } from "./dashboard/sidebar-iconlist/field-filter/field-filter.component";
import { FieldFormatComponent } from "./dashboard/sidebar-iconlist/field-format/field-format.component";
import { DataMartNewComponent } from "./dashboard/dashboard-properties/data-mart-new/data-mart-new.component";
import { BarChartsComponent } from "./dashboard/bar-charts/bar-charts.component";
import { PaneDownloadComponent } from "./dashboard/dialog-group/download/index.component";


@NgModule({
  declarations: [
    CommonGridComponent,
    GridActionComponent,
    GridActionItemComponent,
    DateTreeFilterPopoverComponent,
    FloatBoxComponent,
    RsPageListComponent,
    RsPageTabComponent,
    GridBoxComponent,
    RSHeaderComponent,
    RSFooterComponent,
    RSAsideComponent,
    MultiTabComponent,
    MainContainerComponent,
    TagInputComponent,
    RadioGroupComponent,
    CheckboxGroupComponent,
    RichtexteditorComponent,
    SwitchInputComponent,
    EncryptedInputComponent,
    SearchInputComponent,
    ConfirmSelectComponent,
    NewActionNotificationComponent,
    CommonDialogComponent,
    CommonDeleteComponent,
    DrawerComponent,
    DrawerFormComponent,
    ToolbarItemComponent,
    RsCommentaryComponent,
    RSNavCardGroupComponent,
    RSToolbarComponent,
    TruncatedTextToggleComponent,
    SmartPopupComponent,
    RSStepperComponent,
    PaneGroupNewComponent,
    SidebarIconlistNewComponent,
    EmptyIconPromptComponent,
    GadgetTableComponent,
    GadgetPivotComponent,
    GadgetPivotChartComponent,
    GadgetTransposeComponent,
    PortletTypeNewComponent,
    FieldFilterComponent,
    FieldFormatComponent,
    DataMartNewComponent,
    BarChartsComponent,
    PaneDownloadComponent
  ],
  imports: [
    CommonModule,
    HttpClientModule,
    ReactiveFormsModule,
    FormsModule,
    DragDropModule,
    MatSnackBarModule,
    MatMenuModule,
    MatTooltipModule,
    MatDialogModule,
    MatIconModule,
    MatCheckboxModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    GridModule,
    PagerModule,
    GridAllModule,
    SwitchModule,
    CheckBoxModule,
    RadioButtonModule,
    ButtonModule,
    ChipListModule,
    DiagramModule,
    TooltipModule,
    DialogModule,
    DatePickerModule,
    DateRangePickerModule,
    DateTimePickerModule,
    TextBoxModule,
    ColorPickerModule,
    UploaderModule,
    NumericTextBoxModule,
    DropDownListModule,
    DropDownTreeModule,
    MultiSelectAllModule,
    AutoCompleteModule,
    ListBoxModule,
    PivotViewAllModule,
    PivotFieldListAllModule,
    ChartAllModule,
    AccumulationChartAllModule,
    RangeNavigatorAllModule,
    AccumulationChartModule,
    DashboardLayoutModule,
    CarouselAllModule,
    ToolbarModule,
    TabModule,
    TreeViewModule,
    PdfViewerModule,
    DropDownButtonModule,
    SplitButtonModule,
    RichTextEditorAllModule,
    InlineSVGModule,
  ],
  providers: [
    PageService,
    RowDDService,
    SortService,
    FilterService,
    ExcelExportService,
    EditService,
    ResizeService,
    ToolbarService,
    ColumnChooserService,
    AggregateService,
    ColumnMenuService,
    DetailRowService,
    SelectionService,
    GroupService,
    PieSeriesService,
    AccumulationLegendService,
    AccumulationTooltipService,
    AccumulationAnnotationService,
    AccumulationDataLabelService,
    LinkAnnotationService,
    BookmarkViewService,
    MagnificationService,
    ThumbnailViewService,
    PbfToolbarService,
    NavigationService,
    TextSearchService,
    TextSelectionService,
    PrintService,
    HtmlEditorService,
    RichEditorToolbarService,

    CommonFunctionService,
    IconLoaderService,
    DialogService,
    TranslationService,
  ],
  exports: [
    CommonModule,
    HttpClientModule,
    ReactiveFormsModule,
    FormsModule,
    DragDropModule,
    MatSnackBarModule,
    MatMenuModule,
    MatTooltipModule,
    MatDialogModule,
    GridModule,
    PagerModule,
    GridAllModule,
    SwitchModule,
    CheckBoxModule,
    RadioButtonModule,
    ButtonModule,
    ChipListModule,
    DiagramModule,
    TooltipModule,
    DialogModule,
    DatePickerModule,
    DateRangePickerModule,
    DateTimePickerModule,
    TextBoxModule,
    ColorPickerModule,
    UploaderModule,
    NumericTextBoxModule,
    DropDownListModule,
    DropDownTreeModule,
    MultiSelectAllModule,
    AutoCompleteModule,
    ListBoxModule,
    PivotViewAllModule,
    PivotFieldListAllModule,
    ChartAllModule,
    AccumulationChartAllModule,
    RangeNavigatorAllModule,
    AccumulationChartModule,
    DashboardLayoutModule,
    CarouselAllModule,
    ToolbarModule,
    TabModule,
    TreeViewModule,
    PdfViewerModule,
    DropDownButtonModule,
    SplitButtonModule,
    RichTextEditorAllModule,
    CommonGridComponent,
    GridActionComponent,
    GridActionItemComponent,
    DateTreeFilterPopoverComponent,
    FloatBoxComponent,
    RsPageListComponent,
    RsPageTabComponent,
    GridBoxComponent,
    RSHeaderComponent,
    RSFooterComponent,
    RSAsideComponent,
    MultiTabComponent,
    MainContainerComponent,
    TagInputComponent,
    RadioGroupComponent,
    CheckboxGroupComponent,
    RichtexteditorComponent,
    SwitchInputComponent,
    EncryptedInputComponent,
    SearchInputComponent,
    ConfirmSelectComponent,
    DrawerComponent,
    DrawerFormComponent,
    ToolbarItemComponent,
    RsCommentaryComponent,
    RSNavCardGroupComponent,
    RSToolbarComponent,
    TruncatedTextToggleComponent,
    SmartPopupComponent,
    CommonDialogComponent,
    RSStepperComponent,
    PaneGroupNewComponent,
    SidebarIconlistNewComponent,
    EmptyIconPromptComponent,
    GadgetTableComponent,
    GadgetPivotComponent,
    GadgetPivotChartComponent,
    GadgetTransposeComponent,
    PortletTypeNewComponent,
    FieldFilterComponent,
    FieldFormatComponent,
    DataMartNewComponent,
    BarChartsComponent,
    PaneDownloadComponent
  ],
  entryComponents: [
    NewActionNotificationComponent,
    CommonDialogComponent,
    CommonDeleteComponent,
    FieldFormatComponent,
    DataMartNewComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RaiseCommonLibModule {}
